import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { type Severity, SeverityTag } from '@client/components/_ui/display/tags';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEEP_LIST_OFFSET_LIMIT, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { CoiFilters } from './CoiFilters';
export const Route = createFileRoute('/console/$orgId/compliance/coi/')({
    staticData: {
        ui: {
            title: 'Conflict of interest',
            subtitle: 'Manage and track conflicts of interest',
            widthVariant: 'fullWidth',
        },
    },
    component: CoiSearch,
});

function CoiSearch() {
    const orgId = useOrgId();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    const [queryParams, setQueryParams] = usePathState<RouterInputs['conflictOfInterest']['listConflictsOfInterest']>(
        'query-params',
        {
            orderBy: { createdAt: 'desc' },
            limit: DEFAULT_PAGE_SIZE,
        },
    );

    const { data, isPending, refetch } = trpc.conflictOfInterest.listConflictsOfInterest.useQuery(queryParams);

    type Row = RouterOutputs['conflictOfInterest']['listConflictsOfInterest']['rows'][number];
    type OrderBy = NonNullable<RouterInputs['conflictOfInterest']['listConflictsOfInterest']['orderBy']>;
    const columnHelper = createColumnHelper<Row, OrderBy>();

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
        }),
        columnHelper.accessor('dtAdvised', {
            id: 'dtAdvised',
            header: 'Date advised',
            sortable: 'dtAdvised',
            cell: ({ value }) => <>{formatDate(value)}</>,
        }),
        columnHelper.accessor('severity', {
            id: 'severity',
            header: 'Severity',
            sortable: 'severity',
            cell: ({ value }) => <SeverityTag value={value as Severity} />,
        }),
        columnHelper.accessor('firstName', {
            id: 'firstName',
            header: 'Name',
            sortable: 'firstName',
        }),
        columnHelper.accessor('lastName', {
            id: 'lastName',
            header: 'Surname',
            sortable: 'lastName',
        }),
        columnHelper.accessor('position', {
            id: 'position',
            header: 'Position',
            sortable: 'position',
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created at',
            sortable: 'createdAt',
            cell: ({ value }) => formatDate(value, 'date-time'),
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.OpenInNewTab
                    to="/console/$orgId/compliance/coi/details/$id/manage"
                    params={{ orgId, id: row.id }}
                    tooltip="Open in new tab"
                />
            ),
            width: 50,
        }),
    ];

    const tableProps = usePrismaTable<
        Row,
        RouterInputs['conflictOfInterest']['listConflictsOfInterest']['where'],
        OrderBy
    >({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No conflicts of interest yet' },
        pagination: { position: 'both', maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT },
        columnVisibility: { enabled: true, persistKey: 'coi-table-columns', enableReordering: true },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    return (
        <PageController route={Route}>
            <TableToolbar
                filters={<CoiFilters onChange={(where) => setQueryParams((prev) => ({ ...prev, where, offset: 0 }))} />}
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/compliance/coi/add"
                        params={{ orgId }}
                        variant="primary"
                        label="New conflict of interest"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/compliance/coi/details/$id/manage',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
